import { parseDocument } from 'yaml';

// Client-side parsing helpers used by the Create Resource page for live
// previews. These mirror the server-side validators where it's useful for
// fast feedback; the server remains the source of truth and rejects bad
// inputs on submit.

// Parse a memory string the same way the default Ram convertor does
// (pkg/memory/rammemory.go — github.com/docker/go-units RAMInBytes). All
// suffixes are treated as binary: 1k = 1024, 1KB = 1024, 1Ki = 1024, etc.
// Returns null on parse failure.
//
// The grammar accepted is: number, optional decimal, optional unit. Units:
// b, k, kb, kib, m, mb, mib, g, gb, gib, t, tb, tib, p, pb, pib (any case).
// Suffix-less inputs are bytes.
export function parseMemoryBytes(raw: string): number | null {
  if (!raw) return null;
  const s = raw.trim();
  const match = /^([+-]?\d+(?:\.\d+)?)\s*([kmgtp]?i?b?)?$/i.exec(s);
  if (!match) return null;
  const n = parseFloat(match[1]);
  if (!Number.isFinite(n) || n < 0) return null;
  const unit = (match[2] || '').toLowerCase();
  const exp =
    unit.startsWith('p') ? 5
    : unit.startsWith('t') ? 4
    : unit.startsWith('g') ? 3
    : unit.startsWith('m') ? 2
    : unit.startsWith('k') ? 1
    : 0;
  // Treat everything as binary (matches RAMInBytes default in pkg/memory).
  return Math.round(n * Math.pow(1024, exp));
}

// Format a byte count with thousands separators for the preview line.
export function formatBytes(bytes: number): string {
  return bytes.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

// Live parse of a StringList value. Server-side stores [trimmed empties
// dropped]. We replicate that so the user sees what will actually be saved.
export function parseStringListItems(raw: string): string[] {
  if (!raw) return [];
  return raw.split(/\s+/).filter((s) => s.length > 0);
}

// Result of parsing a Collection payload (YAML or JSON). `error` is set
// when parsing fails; `items` holds the parsed entries when it succeeds.
// `uniform` is true when every entry has the same top-level key set; only
// computed when parsing succeeds.
export interface CollectionParse {
  items: string[];
  error?: string;
  uniform?: boolean;
}

// Parse a Collection payload for live feedback (item count, uniformity,
// syntax errors). JSON goes through JSON.parse; YAML through the `yaml`
// library's parseDocument — the same parser the editor's lint gutter uses,
// so the inline Field error and the on-line squiggle agree. Operates on the
// *raw* user input (before base64). The server remains authoritative on
// submit; this is only to fail fast in the form.
export function parseCollectionLive(
  raw: string,
  format: 'yaml' | 'json',
): CollectionParse {
  const trimmed = raw.trim();
  if (!trimmed) return { items: [], error: 'value is empty' };

  if (format === 'json') {
    try {
      const parsed = JSON.parse(trimmed);
      if (!Array.isArray(parsed)) {
        return { items: [], error: 'JSON must be an array' };
      }
      if (parsed.length === 0) {
        return { items: [], error: 'collection must have at least one item' };
      }
      const stringified = parsed.map((it) =>
        typeof it === 'string' ? it : JSON.stringify(it),
      );
      return { items: stringified, uniform: jsonItemsUniform(parsed) };
    } catch (e) {
      return { items: [], error: `JSON parse error: ${(e as Error).message}` };
    }
  }

  // YAML — parse with the `yaml` library (the same one that backs the
  // editor's lint gutter) so syntax errors are reported accurately instead
  // of the old column-0 "- " heuristic, which silently accepted malformed
  // input. parseDocument never throws; it collects errors on the document.
  const doc = parseDocument(trimmed);
  if (doc.errors.length > 0) {
    return { items: [], error: `YAML parse error: ${doc.errors[0].message}` };
  }
  const js = doc.toJS();
  if (!Array.isArray(js)) {
    return {
      items: [],
      error: 'YAML must be a block sequence (each item starts with "- ")',
    };
  }
  if (js.length === 0) {
    return { items: [], error: 'collection must have at least one item' };
  }
  // Reuse the JSON uniformity check against the parsed structure — more
  // faithful than the old text-key heuristic, and one code path for both
  // formats now that YAML is parsed to the same shape.
  const items = js.map((it) => (typeof it === 'string' ? it : JSON.stringify(it)));
  return { items, uniform: jsonItemsUniform(js) };
}

// A syntax diagnostic with character offsets into the source, shaped for
// CodeMirror's linter (from/to are absolute offsets). Kept here, free of any
// CodeMirror import, so the lint logic lives next to parseCollectionLive and
// the heavy editor stays in its own lazy chunk.
export interface LintDiagnostic {
  from: number;
  to: number;
  message: string;
}

// YAML syntax diagnostics via the `yaml` parser. Empty input is not an
// error (the form's required-field check handles "missing value"). Used by
// the Collection editor's YAML linter; JSON uses CodeMirror's built-in
// jsonParseLinter instead.
export function yamlLintDiagnostics(text: string): LintDiagnostic[] {
  if (text.trim() === '') return [];
  const doc = parseDocument(text);
  return doc.errors.map((e) => {
    const [from, to] = e.pos ?? [0, text.length];
    return { from, to: Math.max(to, from + 1), message: e.message };
  });
}

// Loose uniformity check for JSON arrays of objects: are all top-level
// keys identical across items? Strings/numbers/arrays are considered
// uniform-by-shape (all-strings vs all-numbers etc).
function jsonItemsUniform(items: unknown[]): boolean {
  if (items.length === 0) return true;
  const first = items[0];
  if (first === null || typeof first !== 'object' || Array.isArray(first)) {
    return items.every((it) => typeof it === typeof first);
  }
  const keys = Object.keys(first as object).sort().join(',');
  return items.every((it) => {
    if (it === null || typeof it !== 'object' || Array.isArray(it)) return false;
    return Object.keys(it as object).sort().join(',') === keys;
  });
}

// Tiny ASCII-safe base64 encoder. btoa fails on multi-byte UTF-8; the
// TextEncoder + uint8 → binary string dance handles that. Mirrors the
// server's encoding/base64.StdEncoding for the wire payload.
export function toBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin);
}

// Inverse of toBase64 with the same UTF-8 awareness. Returns the input
// unchanged when atob throws — the API is supposed to send base64, but
// some legacy paths emit plain text and we'd rather show that than a
// crash. The fallback also covers freshly typed values during edit.
export function fromBase64(value: string): string {
  if (!value) return '';
  try {
    const bin = atob(value);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  } catch {
    return value;
  }
}

export interface CollectionAllocationItem {
  index: number;
  value: string;
}

// A Collection allocation ships the items it reserved inside the usage's
// `stringList["0"]`, base64-encoded JSON of the form
// {"items":[{"key":<index>,"value":"..."}], ...} — see
// internal/db/resource/converter/collectionconverter.go
// getAmountFromStringList. `key` is the item's slice index in the
// collection (its `amount` is just items.length). Returns the allocated
// items in allocation order, or null when the blob is absent or
// unparseable, so callers can fall back to showing the amount alone.
export function parseCollectionAllocation(
  raw: string | undefined,
): CollectionAllocationItem[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(fromBase64(raw)) as {
      items?: Array<{ key?: number; value?: string }>;
    };
    if (!Array.isArray(parsed.items)) return null;
    return parsed.items.map((it) => ({
      index: Number(it.key ?? 0),
      value: String(it.value ?? ''),
    }));
  } catch {
    return null;
  }
}

// nameCheck mirror (internal/handler/v1/resource/validator/name.go):
// non-empty, ≤ 255 chars, no whitespace, no slashes (to keep URLs unambiguous).
// Returns null if OK, otherwise the error message.
export function validateResourceName(name: string): string | null {
  if (!name) return 'Name is required';
  if (name.length > 255) return 'Name is too long (max 255 characters)';
  if (/\s/.test(name)) return 'Name cannot contain spaces';
  if (name.includes('/')) return 'Name cannot contain slashes';
  return null;
}
